import React, { useState, useEffect, useMemo } from 'react'
import { api } from '../api'
import ExportButton, { AllSlidesButton } from '../components/ExportButton'
import LastUpdated from '../components/LastUpdated'

const CYCLE_START = new Date('2026-03-09T00:00:00')
const CYCLE_END = new Date('2026-05-15T23:59:59')
const TOTAL_WEEKS = 8 // Working weeks W1-W8 (W0 is planning, not counted)

function getCurrentWeek() {
  const now = new Date()
  const diff = now - CYCLE_START
  if (diff < 0) return 0
  return Math.min(Math.floor(diff / (7 * 24 * 60 * 60 * 1000)), TOTAL_WEEKS) // 0=W0, 1=W1, ..., 8=W8
}

const statusColors = {
  'Done': '#2E7D32',
  'Dev': '#60A5FA',
  'RFC': '#FBBF24',
  'External Flight': '#34D399',
  'Beta Flight': '#34D399',
  'Not started': '#9CA3AF',
  'Cut': '#9CA3AF',
  'TBD': '#9CA3AF',
  'GA': '#34D399',
}

const statusEmoji = {
  'Done': '✅', 'Dev': '🟡', 'RFC': '🟡', 'External Flight': '🟢', 'Beta Flight': '🟢',
  'Not started': '📋', 'Cut': '❌', 'blocked': '🔴', 'active': '🟡',
  'at-risk': '🟡', 'in-progress': '🔵', 'resolved': '✅', 'GA': '✅',
}

const AVATAR_COLORS = ['#0078D4', '#8764B8', '#107C10', '#FF8C00', '#D13438', '#00BCD4', '#E91E63', '#3F51B5']

export default function Dashboard() {
  const [summary, setSummary] = useState(null)
  const [loading, setLoading] = useState(true)

  const currentWeek = useMemo(() => getCurrentWeek(), [])
  // W0 = planning week, doesn't count toward progress. Progress is based on W1-W8.
  const progressPct = (currentWeek / TOTAL_WEEKS) * 100

  useEffect(() => {
    api.getDashboardSummary().then(setSummary).catch(console.error).finally(() => setLoading(false))
  }, [])

  if (loading) return <div className="loading">Loading dashboard...</div>
  if (!summary) return <div className="error">Failed to load dashboard</div>

  const { statusCounts, p0ByMission, memberWorkload, activeExecutiveIssues, totalWorkItems } = summary

  return (
    <div className="dashboard fade-in">
      <div className="cycle-header">
        <div>
          <h2 className="cycle-title">Discover Canvas Squad · C2</h2>
          <div className="cycle-subtitle">March 9 – May 15, 2026</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <LastUpdated data={summary} />
          <ExportButton data={summary} filename="dashboard" tabType="dashboard" />
          <AllSlidesButton />
        </div>
      </div>

      {/* Cycle Progress */}
      <div className="cycle-progress-bar">
        <div className="progress-label">
          <span>Cycle Progress</span>
          <span>{currentWeek === 0 ? 'W0 Planning' : `Week ${currentWeek} of ${TOTAL_WEEKS}`}</span>
        </div>
        <div className="progress-track">
          <div className="progress-fill" style={{ width: `${progressPct}%` }} />
        </div>
      </div>

      {/* Status Summary Cards */}
      <div className="summary-cards">
        {Object.entries(statusCounts).sort((a, b) => b[1] - a[1]).map(([status, count]) => (
          <div key={status} className="summary-card">
            <div className="card-count" style={{ color: statusColors[status] || '#666' }}>{count}</div>
            <div className="card-label">{statusEmoji[status] || '📌'} {status}</div>
          </div>
        ))}
        <div className="summary-card">
          <div className="card-count">{totalWorkItems}</div>
          <div className="card-label">📊 Total</div>
        </div>
      </div>

      {/* Bento Grid */}
      <div className="dashboard-grid">
        <div className="dashboard-section">
          <h3>🚨 Attention Needed</h3>
          {activeExecutiveIssues.length === 0 ? <p className="empty">No active issues — all clear! 🎉</p> :
            activeExecutiveIssues.map(issue => (
              <div key={issue.id} className="exec-card-mini">
                <span className="exec-status">{statusEmoji[issue.status] || '📌'}</span>
                <div>
                  <strong style={{ color: 'var(--text-primary)' }}>{issue.title}</strong>
                  <span className={`badge priority-${issue.priority?.toLowerCase()}`} style={{ marginLeft: 8 }}>{issue.priority}</span>
                  <div className="exec-meta">{issue.owner} · {issue.dueDate || 'No due date'}</div>
                </div>
              </div>
            ))
          }
        </div>

        <div className="dashboard-section">
          <h3>🎯 P0 by Mission</h3>
          <div className="mission-bars">
            {Object.entries(p0ByMission).sort((a, b) => b[1] - a[1]).map(([mission, count]) => (
              <div key={mission} className="mission-bar">
                <span className="mission-name">{mission}</span>
                <div className="bar-container">
                  <div className="bar" style={{ width: `${(count / Math.max(...Object.values(p0ByMission))) * 100}%` }}>{count}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="dashboard-section full-width">
          <h3>👥 Member Workload</h3>
          <div className="workload-list">
            {memberWorkload.slice(0, 10).map((m, i) => (
              <div key={m.alias} className="workload-item">
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div className="workload-avatar" style={{ background: AVATAR_COLORS[i % AVATAR_COLORS.length] }}>
                    {m.fullName?.charAt(0) || '?'}
                  </div>
                  <span style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{m.fullName}</span>
                </div>
                <span className="workload-count">{m.itemCount} items</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
